import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import size from '@/assets/theme/size';

const { laptop } = size;

type UseNavSizeProps = {
	containerRef: React.RefObject<HTMLElement>;
	offsetRef?: HTMLDivElement | null;
	buffer?: number;
};

const useNavSize = ({ containerRef, offsetRef }: UseNavSizeProps) => {
	const [hideNavLabel, setHideNavLabel] = useState<boolean | undefined>(
		undefined,
	);
	const [originalNavWidth, setOriginalNavWidth] = useState<
		number | undefined
	>(undefined);
	const initialLoadRef = useRef(true);
	const initialMobileRef = useRef(false);
	const previousWidthRef = useRef<number>(0);
	const resizeTimeoutRef = useRef<number | null>(null);

	const setNavTransition = useCallback(
		(enable: boolean) => {
			if (!containerRef.current) return;

			const currentValue =
				containerRef.current.style.getPropertyValue('--nav-transition');
			const newValue = enable
				? 'opacity 200ms ease-in-out, max-width 300ms ease-in-out, width 300ms ease-in-out allow-discrete, display 300ms ease-in-out allow-discrete, gap 300ms ease-in-out'
				: 'none';

			if (currentValue !== newValue) {
				containerRef.current.style.setProperty(
					'--nav-transition',
					newValue,
				);
			}
		},
		[containerRef],
	);

	const checkSpace = useCallback(() => {
		if (!originalNavWidth || !containerRef.current) return;

		const logoWidth = offsetRef?.offsetWidth ?? 0;
		const availableWidth = window.innerWidth / 2 - logoWidth / 2;
		const shouldHide = originalNavWidth > availableWidth;
		const currentWidth = window.innerWidth;

		if (hideNavLabel !== shouldHide) {
			const shouldDisableTransition =
				initialLoadRef.current ||
				(initialMobileRef.current && currentWidth <= laptop);

			const shouldEnableTransition =
				!shouldDisableTransition &&
				((currentWidth < previousWidthRef.current && shouldHide) ||
					(hideNavLabel === true &&
						!shouldHide &&
						currentWidth > laptop));

			setNavTransition(shouldEnableTransition);
			setHideNavLabel(shouldHide);
		}

		previousWidthRef.current = currentWidth;
	}, [
		originalNavWidth,
		offsetRef,
		containerRef,
		laptop,
		hideNavLabel,
		setNavTransition,
	]);

	const updateOriginalNavWidth = useCallback(() => {
		if (!containerRef.current) return;

		const currentWidth = containerRef.current.offsetWidth;

		if (
			currentWidth > 0 &&
			(!originalNavWidth || currentWidth > originalNavWidth)
		) {
			setOriginalNavWidth(currentWidth);
		} else if (!originalNavWidth && currentWidth === 0) {
			requestAnimationFrame(updateOriginalNavWidth);
		}
	}, [containerRef, originalNavWidth]);

	useLayoutEffect(() => {
		setNavTransition(false);
		previousWidthRef.current = window.innerWidth;

		if (window.innerWidth < laptop) {
			initialMobileRef.current = true;
			setHideNavLabel(true);
		}

		const timer = setTimeout(() => {
			initialLoadRef.current = false;
		}, 500);

		return () => clearTimeout(timer);
	}, [setNavTransition, laptop]);

	useLayoutEffect(() => {
		updateOriginalNavWidth();
	}, [updateOriginalNavWidth]);

	useLayoutEffect(() => {
		const handleResize = () => {
			if (resizeTimeoutRef.current !== null) {
				cancelAnimationFrame(resizeTimeoutRef.current);
			}

			resizeTimeoutRef.current = requestAnimationFrame(() => {
				updateOriginalNavWidth();
				checkSpace();
				resizeTimeoutRef.current = null;
			});
		};

		handleResize();

		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
			if (resizeTimeoutRef.current !== null) {
				cancelAnimationFrame(resizeTimeoutRef.current);
			}
		};
	}, [updateOriginalNavWidth, checkSpace]);

	return { hideNavLabel, navWidth: originalNavWidth, checkSpace };
};

export default useNavSize;
